import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthenticationService } from '@core/services';
import { COMPANY_ID_KEY, CompanyService } from '@core/services/company.service';
import { LoadingBarService } from '@core/services/loading-bar.service';
import { BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, throwError } from 'rxjs';

// Singleton compartilhado entre todas as execuções do interceptor funcional.
let isRefreshing = false;
const refreshToken$ = new BehaviorSubject<string | null>(null);

/** Retorna true se o JWT está expirado ou expira nos próximos 30 segundos. */
function isTokenExpired(token: string): boolean {
	try {
		const payload = JSON.parse(atob(token.split('.')[1]));
		return Date.now() / 1000 >= payload.exp - 30;
	} catch {
		return true;
	}
}

/**
 * Executa o refresh do token e retenta a request original com o novo token.
 * Requests concorrentes aguardam no BehaviorSubject até o refresh completar.
 */
function refreshAndRetry(
	req: HttpRequest<unknown>,
	next: Parameters<HttpInterceptorFn>[1],
	authService: AuthenticationService,
	loadingBarService: LoadingBarService,
	companyService: CompanyService
): Observable<HttpEvent<unknown>> {
	if (isRefreshing) {
		return refreshToken$.pipe(
			filter((t): t is string => t !== null),
			take(1),
			switchMap((newToken) => next(addToken(req, newToken, companyService)))
		);
	}

	isRefreshing = true;
	refreshToken$.next(null);
	loadingBarService.show();

	return authService.refreshAccessToken().pipe(
		switchMap((newToken: string) => {
			isRefreshing = false;
			refreshToken$.next(newToken);
			return next(addToken(req, newToken, companyService));
		}),
		catchError((refreshError) => {
			isRefreshing = false;
			refreshToken$.next(null);
			// Faz logout apenas quando o servidor rejeita o próprio refresh (4xx).
			// Erros temporários de rede (5xx) não devem deslogar.
			if (refreshError instanceof HttpErrorResponse && refreshError.status >= 400 && refreshError.status < 500) {
				authService.logout();
			}
			return throwError(() => refreshError);
		}),
		finalize(() => loadingBarService.hide())
	);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
	const authService = inject(AuthenticationService);
	const loadingBarService = inject(LoadingBarService);
	const companyService = inject(CompanyService);

	if (
		req.url.includes('/RefreshToken') ||
		// O portal do cliente é público: não leva o login de quem abre e roda no servidor (SSR).
		req.url.includes('/v1/Public/') ||
		req.url.includes('viacep.com.br') ||
		req.url.includes('blob.core.windows.net')
	) {
		return next(req);
	}

	const token = authService.getToken();

	// Verificação proativa: refresh antes de enviar a request se o token está expirado.
	if (token && isTokenExpired(token) && authService.getRefreshToken()) {
		return refreshAndRetry(req, next, authService, loadingBarService, companyService);
	}

	if (token) {
		req = addToken(req, token, companyService);
	}

	return next(req).pipe(
		catchError((error) => {
			// Fallback reativo para 401 (ex: diferença de relógio entre cliente e servidor).
			if (error instanceof HttpErrorResponse && error.status === 401 && authService.getRefreshToken()) {
				return refreshAndRetry(req, next, authService, loadingBarService, companyService);
			}
			return throwError(() => error);
		})
	);
};

const addToken = (req: HttpRequest<unknown>, token: string, companyService: CompanyService) => {
	// Usa o signal se já carregado; caso contrário, cai no localStorage (evita
	// race condition entre loadAllCompanies() e requests dos filhos)
	const companyId = companyService.currentCompany()?.id ?? localStorage.getItem(COMPANY_ID_KEY);

	const headers: Record<string, string> = {
		Authorization: `Bearer ${token}`,
	};
	/* Quem já disse o escritório (ex.: sair de outro escritório) manda no cabeçalho. */
	if (companyId && !req.headers.has('x-company-id')) {
		headers['x-Company-Id'] = companyId;
	}

	return req.clone({
		setHeaders: headers,
	});
};
